import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fmtMoney, convert, CURRENCIES } from "../lib/api";
import { useCurrency } from "../contexts/CurrencyContext";
import usePricing from "../hooks/usePricing";
import BulkQuoteModal from "./BulkQuoteModal";
import BulkAllocationTable from "./BulkAllocationTable";

const MIN_USD = 100_000;
const QUICK_USD = [100_000, 1_000_000, 5_000_000, 25_000_000];

export default function BulkBuyPanel() {
  const { t } = useTranslation();
  const { currency: globalCur } = useCurrency();
  const [pool, setPool] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [showQuote, setShowQuote] = useState(false);

  const pricing = usePricing({ initialPayCur: globalCur, initialTons: 0 });
  const { tons, amount, payCur, indexUsd, totalUsd, setTons, setAmount, setPayCur, setLastEdited } = pricing;

  useEffect(() => { setPayCur(globalCur); }, [globalCur, setPayCur]);

  useEffect(() => {
    api.get("/bulk/settings/public").then((r) => setPool(r.data)).catch(() => {});
  }, []);

  // Refresh allocation preview whenever the inputs settle.
  useEffect(() => {
    if (totalUsd < MIN_USD) { setAllocation(null); return; }
    let cancelled = false;
    const handle = setTimeout(() => {
      api.post("/bulk/preview", { total_usd: totalUsd }).then((r) => {
        if (!cancelled) setAllocation(r.data);
      }).catch(() => {});
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [totalUsd]);

  const minAmount = Number(convert(MIN_USD, payCur).toFixed(2));
  const minTonsLabel = Math.ceil(MIN_USD / indexUsd);
  const belowMin = totalUsd < MIN_USD;
  const overPool = pool && totalUsd > pool.available_for_auto_usd;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10" data-testid="bulk-panel">
      <div className="border border-ink bg-surface">
        <div className="px-6 py-5 border-b border-line flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="label-tag">{t("bulk.tag")}</div>
            <div className="font-serif text-2xl mt-1">{t("bulk.heading")}</div>
            <p className="text-xs text-ink2 mt-1 max-w-2xl">{t("bulk.sub")}</p>
          </div>
          {pool && (
            <div className="text-right">
              <div className="label-tag">{t("bulk.portfolio_available")}</div>
              <div className="mono-num text-2xl text-ink mt-1">
                {fmtMoney(pool.available_for_auto_usd, "USD")}
              </div>
              <div className="text-[11px] font-mono text-ink2 mt-0.5">
                {t("bulk.across_n", { count: pool.selected_count })} · <Link to="/registry" className="underline">{t("bulk.view_registry")} ↗</Link>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tag block mb-1">{t("bulk.tons_label")}</label>
                <input
                  data-testid="bulk-tons"
                  type="number"
                  min={minTonsLabel}
                  step="1"
                  value={tons || ""}
                  placeholder={minTonsLabel.toLocaleString()}
                  onChange={(e) => { setLastEdited("tons"); setTons(e.target.value); }}
                  className="input-field"
                />
                <div className="text-[10px] font-mono text-ink2 mt-1">{t("bulk.min")} {minTonsLabel.toLocaleString()} t</div>
              </div>
              <div>
                <label className="label-tag block mb-1">{t("bulk.total_label", { currency: payCur })}</label>
                <input
                  data-testid="bulk-amount"
                  type="number"
                  min={minAmount}
                  step="1000"
                  value={amount}
                  placeholder={fmtMoney(minAmount, payCur)}
                  onChange={(e) => { setLastEdited("amount"); setAmount(e.target.value); }}
                  className="input-field"
                />
                <div className="text-[10px] font-mono text-ink2 mt-1">{t("bulk.min")} {fmtMoney(minAmount, payCur)}</div>
              </div>
            </div>

            <div>
              <label className="label-tag block mb-1">{t("bulk.pay_with")}</label>
              <select data-testid="bulk-currency" value={payCur} onChange={(e) => setPayCur(e.target.value)} className="input-field">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="flex items-center flex-wrap gap-1">
              <span className="label-tag mr-1">{t("bulk.quick_fill")}</span>
              {QUICK_USD.map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => { setLastEdited("amount"); setAmount(convert(u, payCur).toFixed(2)); }}
                  data-testid={`bulk-preset-${u}`}
                  className="px-2 py-1 text-[10px] font-mono uppercase border border-line text-ink2 hover:border-ink hover:text-ink"
                >
                  {u >= 1_000_000 ? `${u / 1_000_000}M` : `${u / 1_000}k`} USD
                </button>
              ))}
            </div>

            <button
              type="button"
              disabled={belowMin}
              onClick={() => setShowQuote(true)}
              data-testid="bulk-request"
              className="btn-primary w-full disabled:opacity-50"
            >
              {belowMin ? t("bulk.min_required", { amount: fmtMoney(MIN_USD, "USD") }) : `${t("bulk.request_quote")} →`}
            </button>
          </div>

          <div className="lg:col-span-7">
            <div className="label-tag mb-2">{t("bulk.allocation_preview")}</div>
            <BulkAllocationTable allocation={allocation} minUsd={MIN_USD} overPool={overPool} />
          </div>
        </div>
      </div>

      {showQuote && (
        <BulkQuoteModal
          totalUsd={totalUsd}
          payCur={payCur}
          indexUsd={indexUsd}
          onClose={() => setShowQuote(false)}
        />
      )}
    </section>
  );
}
